import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Award,
  BadgeCheck,
  BarChart3,
  Bell,
  CalendarCheck,
  ClipboardList,
  Compass,
  Cpu,
  CreditCard,
  HeartHandshake,
  Layers,
  Lightbulb,
  ListChecks,
  MapPin,
  Megaphone,
  ShieldCheck,
  Smartphone,
  Store,
  Telescope,
  TrendingUp,
  Users,
  Wrench,
} from "lucide-react";
import { Eyebrow, MaskLine, Reveal, SectionHeading } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import PhoneMockup from "@/components/PhoneMockup";
import ContactForm from "@/components/ContactForm";
import CtaBand from "@/components/CtaBand";
import NewsSection from "@/components/NewsSection";
import { useContent } from "@/context/ContentContext";
import { CraftsiLogo } from "@/components/Logo";

const HIGHLIGHTS = [
  {
    Icon: BadgeCheck,
    title: "Registered Company",
    text: "Incorporated in India under the Companies Act, 2013 — CIN U47912AP2023PTC110487.",
  },
  {
    Icon: Lightbulb,
    title: "Innovation",
    text: "We build technology-driven solutions designed to solve real-world problems.",
  },
  {
    Icon: HeartHandshake,
    title: "Customer First",
    text: "We focus on creating simple, reliable and valuable experiences.",
  },
  {
    Icon: Telescope,
    title: "Future Focused",
    text: "We continuously evolve our products and technology to create long-term impact.",
  },
];

const VALUES = [
  { Icon: ShieldCheck, title: "Integrity", text: "We operate with transparency, responsibility and trust." },
  { Icon: Lightbulb, title: "Innovation", text: "We continuously look for better ways to solve real problems." },
  { Icon: HeartHandshake, title: "Customer First", text: "We design around the needs of customers and businesses." },
  { Icon: Award, title: "Excellence", text: "We aim for quality and continuous improvement in everything we build." },
];

const FEATURES = [
  { Icon: MapPin, title: "Discover Local Services" },
  { Icon: CalendarCheck, title: "Book & Request Services" },
  { Icon: Store, title: "Business Profiles" },
  { Icon: ClipboardList, title: "Vendor Management" },
  { Icon: Users, title: "Customer Management" },
  { Icon: ListChecks, title: "Service Listings" },
  { Icon: CalendarCheck, title: "Appointments & Job Requests" },
  { Icon: CreditCard, title: "Payments" },
  { Icon: Bell, title: "Notifications" },
  { Icon: Megaphone, title: "Promotions & Sponsored Visibility" },
];

const BUSINESSES = [
  {
    Icon: Store,
    title: "Craftsi",
    badge: "Flagship Platform",
    text: "Local services marketplace and digital business platform.",
    link: "/craftsi",
  },
  {
    Icon: Layers,
    title: "Digital Products",
    badge: "Area of Focus",
    text: "Technology products designed to simplify business and customer experiences.",
  },
  {
    Icon: BarChart3,
    title: "Business Solutions",
    badge: "Area of Focus",
    text: "Scalable solutions that help businesses improve their digital presence and operations.",
  },
];

const WHY = [
  {
    Icon: Cpu,
    title: "Technology Driven",
    text: "We use technology to create practical solutions to real-world problems.",
  },
  {
    Icon: Users,
    title: "Customer Focused",
    text: "Our products are designed around genuine customer and business needs.",
  },
  {
    Icon: TrendingUp,
    title: "Scalable",
    text: "We build platforms with long-term growth and scalability in mind.",
  },
  {
    Icon: Compass,
    title: "Innovation Led",
    text: "We continuously improve and explore new opportunities.",
  },
];

const Hero = () => {
  const content = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pb-24 pt-36 sm:pt-44">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-mist"
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <Eyebrow>Sunkev Enterprises Private Limited</Eyebrow>
          </motion.div>

          <h1 className="mt-7 font-heading text-5xl font-extrabold leading-[1.04] tracking-tight text-ink sm:text-6xl lg:text-7xl">
            <MaskLine delay={0.15}>{content.hero_line_1 || "Building Digital"}</MaskLine>
            <MaskLine delay={0.28}>{content.hero_line_2 || "Solutions."}</MaskLine>
            <MaskLine delay={0.44} className="text-navy">
              {content.hero_line_3 || "Empowering"}
            </MaskLine>
            <MaskLine delay={0.57} className="text-navy">
              {content.hero_line_4 || "Businesses"}
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-fog md:text-lg"
          >
            {content.hero_subtext ||
              "Sunkev Enterprises Private Limited is a technology-driven company focused on building innovative digital products and solutions that simplify the way businesses and customers connect, engage, and grow."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/about"
              data-testid="hero-learn-more"
              className="group inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 font-heading text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 hover:shadow-xl hover:shadow-navy/25"
            >
              {content.hero_btn_primary || "Learn More"}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/craftsi"
              data-testid="hero-explore-craftsi"
              className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-8 py-4 font-heading text-sm font-bold uppercase tracking-widest text-navy transition-all duration-300 hover:border-gold hover:bg-gold/5"
            >
              {content.hero_btn_secondary || "Explore Craftsi"}
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 h-[110%] w-[110%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-mist via-white to-navy-100/70"
          />
          <motion.div style={{ y: cardY }} className="relative flex justify-center py-6">
            <PhoneMockup variant="customer" />
          </motion.div>

          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            data-testid="hero-appointment-card"
            className="absolute left-1 top-10 flex items-center gap-3 rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-xl shadow-navy/10 backdrop-blur-xl sm:-left-4"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy">
              <CalendarCheck className="h-5 w-5 text-gold" />
            </span>
            <div>
              <p className="font-heading text-xs font-bold text-ink">Appointment Confirmed</p>
              <p className="text-[11px] text-fog">Salon at Home · Today, 5:00 PM</p>
            </div>
          </motion.div>

          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            data-testid="hero-job-card"
            className="absolute bottom-28 right-1 flex items-center gap-3 rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-xl shadow-navy/10 backdrop-blur-xl sm:-right-4"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-royal">
              <Wrench className="h-5 w-5 text-white" />
            </span>
            <div>
              <p className="font-heading text-xs font-bold text-ink">Job Request Accepted</p>
              <p className="text-[11px] text-fog">AC Repair · Arriving in 30 min</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="absolute right-2 top-4 hidden flex-col items-end gap-2 sm:flex"
          >
            {["Salon", "Plumber", "Fitness", "Electrician", "Cleaning", "Pet Care", "Tutoring", "AC Repair"].map((c) => (
              <span
                key={c}
                className="flex items-center gap-2 rounded-full border border-gray-100 bg-white/90 px-4 py-1.5 text-[11px] font-semibold text-navy shadow-lg shadow-navy/5 backdrop-blur-xl"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                {c}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.35 }}
            className="absolute -bottom-4 left-2 flex items-center gap-4 rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-xl shadow-navy/10 backdrop-blur-xl sm:left-6"
          >
            <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-navy">
              <img src="/assets/craftsi-logo-white.png" alt="" className="h-6 w-auto" />
            </span>
            <div>
              <p className="font-heading text-sm font-bold text-ink">Craftsi</p>
              <p className="text-xs text-fog">Our flagship platform</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const Highlights = () => (
  <section data-testid="highlights-section" className="pb-8 pt-20">
    <div className="mx-auto grid max-w-7xl gap-6 px-5 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
      {HIGHLIGHTS.map((h, i) => (
        <Reveal key={h.title} delay={i * 0.08}>
          <div className="group h-full rounded-3xl border border-gray-100 bg-white p-8 shadow-sm transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-navy/10">
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/30 bg-mist transition-colors duration-500 group-hover:bg-navy">
              <h.Icon className="h-6 w-6 text-navy transition-colors duration-500 group-hover:text-gold" />
            </div>
            <h3 className="mt-6 font-heading text-lg font-bold text-ink">{h.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-fog">{h.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const AboutSection = () => (
  <section data-testid="about-section" className="py-24 sm:py-32">
    <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
      <div>
        <Reveal>
          <Eyebrow>About Us</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Sunkev Enterprises Private Limited
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-7 text-base leading-relaxed text-fog md:text-lg">
            Sunkev Enterprises Private Limited is a technology-driven company focused on
            developing innovative digital products and platforms that create value for
            businesses, service providers and customers.
          </p>
          <p className="mt-5 text-base leading-relaxed text-fog md:text-lg">
            Through technology, we aim to simplify everyday business interactions, improve
            accessibility and create scalable solutions for the modern economy.
          </p>
        </Reveal>
        <Reveal delay={0.24}>
          <Link
            to="/about"
            data-testid="about-read-more"
            className="group mt-9 inline-flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-widest text-navy"
          >
            Read More
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-navy/20 transition-all duration-300 group-hover:border-gold group-hover:bg-gold group-hover:text-navy-900">
              <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div className="relative overflow-hidden rounded-3xl border border-gray-100 bg-white p-10 shadow-xl shadow-navy/5">
          <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-navy via-royal to-gold" />
          <h3 className="font-heading text-sm font-bold uppercase tracking-[0.24em] text-navy">
            Company Information
          </h3>
          <dl className="mt-8 divide-y divide-gray-100">
            {[
              ["Company Name", "Sunkev Enterprises Private Limited"],
              ["Legal Entity", "Private Limited Company"],
              ["CIN", "U47912AP2023PTC110487"],
              ["Incorporated", "11 March 2023"],
              ["Country", "India"],
              ["Industry", "Technology & Digital Solutions"],
            ].map(([k, v]) => (
              <div key={k} className="flex flex-col gap-1 py-5 sm:flex-row sm:items-center sm:justify-between">
                <dt className="text-xs font-semibold uppercase tracking-wider text-fog">{k}</dt>
                <dd className="font-heading text-base font-bold text-ink sm:text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </Reveal>
    </div>
  </section>
);

const Vision = () => {
  const content = useContent();
  return (
  <section data-testid="vision-section" className="bg-mist py-24 sm:py-32">
    <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
      <Reveal>
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-gold/30 bg-white shadow-lg shadow-navy/5">
          <Compass className="h-9 w-9 text-gold" strokeWidth={1.5} />
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <Eyebrow className="mt-8 justify-center">Our Vision</Eyebrow>
      </Reveal>
      <Reveal delay={0.18}>
        <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          {content.vision_heading || "Building technology that creates meaningful impact."}
        </h2>
      </Reveal>
      <Reveal delay={0.26}>
        <p className="mt-7 text-base leading-relaxed text-fog md:text-lg">
          {content.vision_text ||
            "To build trusted, innovative and scalable digital platforms that make businesses more accessible, efficient and connected."}
        </p>
      </Reveal>
    </div>
  </section>
  );
};

const Values = () => {
  const content = useContent();
  const overrides = {
    Integrity: content.value_integrity,
    Innovation: content.value_innovation,
    "Customer First": content.value_customer_first,
    Excellence: content.value_excellence,
  };
  return (
  <section data-testid="values-section" className="py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHeading
        eyebrow="Our Values"
        title="The principles behind everything we build."
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {VALUES.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.08}>
            <div className="group h-full rounded-3xl border border-gray-100 bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-xl hover:shadow-navy/10">
              <v.Icon className="h-8 w-8 text-navy transition-colors duration-500 group-hover:text-gold" strokeWidth={1.5} />
              <h3 className="mt-6 font-heading text-xl font-bold text-ink">{v.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-fog">{overrides[v.title] || v.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

const CraftsiShowcase = () => (
  <section data-testid="craftsi-section" className="overflow-hidden bg-mist py-24 sm:py-32">
    <div className="mx-auto grid max-w-7xl items-center gap-20 px-5 sm:px-8 lg:grid-cols-2">
      <div>
        <Reveal>
          <CraftsiLogo large />
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-8 font-heading text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
            Craftsi
          </h2>
          <p className="mt-3 font-heading text-lg font-medium text-gold-dark md:text-xl">
            Your Local Marketplace for Every Need
          </p>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-7 text-base leading-relaxed text-fog md:text-lg">
            Craftsi is our flagship digital platform designed to connect customers with
            trusted local businesses and service providers.
          </p>
          <p className="mt-5 text-base leading-relaxed text-fog md:text-lg">
            From home services and beauty to healthcare, education, automotive services,
            household services and other local needs, Craftsi is designed to make
            discovering and accessing services simple and convenient.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {[
            {
              Icon: Smartphone,
              title: "Customer App",
              text: "For customers to discover local businesses, explore services, request or book services and manage their activity.",
            },
            {
              Icon: Store,
              title: "Vendor App",
              text: "For businesses and service providers to manage their business profile, services, bookings, customers and growth.",
            },
          ].map((c, i) => (
            <Reveal key={c.title} delay={0.2 + i * 0.1}>
              <div className="h-full rounded-3xl border border-gray-100 bg-white p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10">
                <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-gold-dark">
                  {c.title}
                </span>
                <c.Icon className="mt-4 h-7 w-7 text-royal" strokeWidth={1.5} />
                <p className="mt-4 text-sm leading-relaxed text-fog">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.35}>
          <Link
            to="/craftsi"
            data-testid="craftsi-explore-button"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 font-heading text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 hover:shadow-xl hover:shadow-navy/25"
          >
            Explore Craftsi
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      <div className="relative mx-auto flex items-end justify-center lg:justify-end">
        <Reveal className="relative z-10 -rotate-2" delay={0.15}>
          <PhoneMockup variant="customer" />
        </Reveal>
        <Reveal className="relative z-0 -ml-16 hidden translate-y-10 rotate-3 sm:block" delay={0.3}>
          <PhoneMockup variant="vendor" />
        </Reveal>
        <div
          aria-hidden="true"
          className="absolute -bottom-10 left-1/2 h-64 w-[130%] -translate-x-1/2 rounded-[100%] bg-navy/5 blur-2xl"
        />
      </div>
    </div>
  </section>
);

const CraftsiFeatures = () => (
  <section data-testid="craftsi-features-section" className="py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHeading
        eyebrow="Craftsi Platform"
        title="One Platform. Multiple Possibilities."
        text="Everything customers and local businesses need to discover, book, manage and grow — in one connected ecosystem."
      />
      <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={(i % 5) * 0.06}>
            <div className="group flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:border-royal/30 hover:shadow-lg hover:shadow-navy/10">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-mist transition-colors duration-500 group-hover:bg-navy">
                <f.Icon className="h-5 w-5 text-royal transition-colors duration-500 group-hover:text-gold" strokeWidth={1.75} />
              </span>
              <h3 className="mt-5 font-heading text-sm font-bold leading-snug text-ink">
                {f.title}
              </h3>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Businesses = () => (
  <section data-testid="businesses-section" className="bg-mist py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHeading
        eyebrow="What We Build"
        title="Our Businesses"
        text="Sunkev Enterprises is focused on building and developing businesses and digital solutions that address real-world needs."
      />
      <div className="mt-14 grid gap-6 lg:grid-cols-3">
        {BUSINESSES.map((b, i) => (
          <Reveal key={b.title} delay={i * 0.1}>
            <div className="group flex h-full flex-col rounded-3xl border border-gray-100 bg-white p-9 shadow-sm transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-navy/10">
              <div className="flex items-center justify-between">
                <span className="flex h-13 w-13 items-center justify-center rounded-2xl bg-navy p-3.5">
                  <b.Icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                </span>
                <span
                  className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${
                    b.link ? "bg-gold/15 text-gold-dark" : "bg-mist text-fog"
                  }`}
                >
                  {b.badge}
                </span>
              </div>
              <h3 className="mt-7 font-heading text-2xl font-bold text-ink">{b.title}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-fog">{b.text}</p>
              {b.link && (
                <Link
                  to={b.link}
                  className="group/link mt-7 inline-flex items-center gap-2 text-sm font-semibold text-royal"
                >
                  Explore Craftsi
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                </Link>
              )}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section data-testid="why-us-section" className="py-24 sm:py-32">
    <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-[1fr_1.4fr]">
      <div>
        <div className="lg:sticky lg:top-32">
          <SectionHeading
            eyebrow="Why Choose Us"
            title="Why Sunkev Enterprises?"
            text="A technology company built on clarity of purpose — practical products, honest execution and long-term thinking."
          />
        </div>
      </div>
      <div>
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.06}>
            <div className="group flex gap-8 border-t border-gray-100 py-9 transition-colors duration-500 last:border-b hover:bg-mist/60">
              <span className="font-heading text-sm font-bold text-gold">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <w.Icon className="h-5 w-5 text-navy" strokeWidth={1.75} />
                  <h3 className="font-heading text-xl font-bold text-ink">{w.title}</h3>
                </div>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-fog md:text-base">
                  {w.text}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ContactSection = () => {
  const content = useContent();
  return (
  <section data-testid="contact-section" className="bg-white py-24 sm:py-32">
    <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-[1fr_1.3fr]">
      <div>
        <SectionHeading
          eyebrow="Contact Us"
          title={content.contact_heading || "Start a conversation."}
          text={
            content.contact_subtext ||
            "Whether it's a partnership, a business requirement or a question about Craftsi — write to us and our team will respond."
          }
        />
        <div className="mt-10 space-y-5">
          {[
            ["Email", "sunkeventerprises@gmail.com"],
            ["Phone", "+91 93924 81929"],
            ["Address", "19-3-1A/S9, Postal Colony, Korrameenugunta, Renigunta Rd, Tirupati – 517501, Andhra Pradesh, India"],
          ].map(([k, v]) => (
            <Reveal key={k}>
              <div className="flex items-start gap-4 rounded-2xl border border-gray-100 bg-mist p-5">
                <span className="h-2 w-2 shrink-0 translate-y-1.5 rounded-full bg-gold" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-fog">{k}</p>
                  <p className="mt-1 text-sm font-medium text-ink">{v}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={0.15}>
        <ContactForm />
      </Reveal>
    </div>
  </section>
  );
};

const Home = () => (
  <>
    <Hero />
    <Marquee />
    <Highlights />
    <AboutSection />
    <Vision />
    <Values />
    <CraftsiShowcase />
    <CraftsiFeatures />
    <Businesses />
    <WhyUs />
    <NewsSection />
    <CtaBand />
    <ContactSection />
  </>
);

export default Home;
